'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from '@bprogress/next/app'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  EditIcon,
  LanguagesIcon,
  KeyIcon,
  HashIcon,
  TrashIcon,
  ListIcon,
  CheckIcon,
  XIcon,
  TablePropertiesIcon,
  CopyIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ContentDetailInfo, ContentError, DeleteConfirmationDialog } from '@/components/partials'
import { formatDateTime, getCountryCodeFromLocale, hasTranslation } from '@/lib/utils'
import { useProperty, useDeleteProperty, useCopyToClipboard } from '@/hooks'
import { useMetaStore } from '@/store'

type PropsType = {
  id: string
}

const PropertyShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-5 w-12" />
            </div>
            <Skeleton className="h-px w-full" />
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-36" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deletePropertyId, setDeletePropertyId] = useState<string | null>(null)

  const { mutate: deletePropertyMutate, isPending: isDeleting } = useDeleteProperty()
  const { data: property, isLoading, error } = useProperty(id)
  const { metaState } = useMetaStore()
  const locales = metaState.response?.locales

  const handleDelete = () => {
    setDeletePropertyId(id)
  }

  const confirmDeleteProperty = () => {
    if (deletePropertyId) {
      deletePropertyMutate(deletePropertyId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['properties'] })
          toast.success(t('deleted_successfully'))
          router.push('/products/properties')
        },
        onError: error => {
          console.error('Error deleting property:', error)
          toast.error(t('error_deleting'))
          setDeletePropertyId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <PropertyShowSkeleton />
  }

  if (error || !property) {
    return <ContentError error={error} listUrl="/products/properties" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/products/properties">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('property_details')}</h1>
            <p className="text-muted-foreground">{t('view_property_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/products/properties/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              {t('edit_property')}
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            {t('delete')}
          </Button>
        </div>
      </div>

      {/* Property Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TablePropertiesIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {property.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(property.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className="col-span-3 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <KeyIcon className="h-4 w-4" />
                {t('key')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {property.key}
              </Badge>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={property} />
        </CardContent>
      </Card>

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {t('translations')}
          </CardTitle>
          <CardDescription>{t('available_translations')}</CardDescription>
        </CardHeader>
        <CardContent>
          {locales && locales.length > 0 ? (
            <Tabs defaultValue={locales[0].fullCode} className="w-full">
              <TabsList className="min-h-10.5 w-full">
                {locales.map(localeItem => (
                  <TabsTrigger
                    key={localeItem.fullCode}
                    value={localeItem.fullCode}
                    className="flex items-center gap-2"
                    disabled={!hasTranslation(property, localeItem.fullCode)}>
                    <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                    <span className="hidden sm:inline">{localeItem.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {locales.map(localeItem => (
                <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-6 space-y-4">
                  {hasTranslation(property, localeItem.fullCode) ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {localeItem.fullCode}
                        </Badge>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-muted-foreground text-sm font-medium">{t('value')}</label>
                          <div className="bg-muted/50 rounded-lg border p-4">
                            <p className="text-sm leading-relaxed">{property.translations[localeItem.fullCode].name}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('created_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(property.translations[localeItem.fullCode].createdAt, locale)}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('updated_at')}</label>
                            <p className="text-sm">
                              {formatDateTime(property.translations[localeItem.fullCode].updatedAt, locale)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <div className="text-muted-foreground flex items-center justify-center gap-2">
                        <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                        <span>{localeItem.name}</span>
                      </div>
                      <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_languages_available')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fields */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListIcon className="h-5 w-5" />
            {t('fields')}
          </CardTitle>
          <CardDescription>{t('property_fields')}</CardDescription>
        </CardHeader>
        <CardContent>
          {Array.isArray(property.fields) && property.fields.length > 0 ? (
            <div className="space-y-6">
              {property.fields.map(field => (
                <div key={field.id} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {t(`field_type_${field.type}`)}
                        </Badge>
                        {field.required ? (
                          <Badge variant="destructive" className="flex items-center gap-1 text-xs">
                            <CheckIcon className="h-3 w-3" />
                            {t('required')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center gap-1 text-xs">
                            <XIcon className="h-3 w-3" />
                            {t('optional')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {t('sort_order')}: {field.order}
                    </Badge>
                  </div>

                  <Tabs defaultValue={locales?.[0]?.fullCode} className="w-full">
                    <TabsList className="min-h-10.5 w-full">
                      {locales?.map(localeItem => {
                        const hasFieldTranslation = field.translations[localeItem.fullCode] !== undefined
                        return (
                          <TabsTrigger
                            key={localeItem.fullCode}
                            value={localeItem.fullCode}
                            className="flex items-center gap-2"
                            disabled={!hasFieldTranslation}>
                            <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                            <span className="hidden sm:inline">{localeItem.name}</span>
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales?.map(localeItem => {
                      const fieldTranslation = field.translations[localeItem.fullCode]
                      return (
                        <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-4">
                          {fieldTranslation ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                                <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                                <Badge variant="outline" className="text-xs">
                                  {localeItem.fullCode}
                                </Badge>
                              </div>
                              <Separator />
                              <div className="space-y-2">
                                <label className="text-muted-foreground text-sm font-medium">{t('name')}</label>
                                <div className="bg-muted/50 rounded-lg border p-4">
                                  <p className="text-sm leading-relaxed">{fieldTranslation.name}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="py-4 text-center">
                              <div className="text-muted-foreground flex items-center justify-center gap-2">
                                <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                                <span>{localeItem.name}</span>
                              </div>
                              <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                            </div>
                          )}
                        </TabsContent>
                      )
                    })}
                  </Tabs>

                  <Separator />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_fields_added')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deletePropertyId !== null}
        onCancel={() => setDeletePropertyId(null)}
        onConfirm={confirmDeleteProperty}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
